import React from 'react';
import axios from 'axios';

import { Company, CompanyEmployee, Phone, PhoneType, User } from '@homesusa/core';
import { useAuth } from '../providers';
import { MarketHeader } from '../utils/axios';
import { AuthAppContext } from '../providers/auth-app/auth-app.context';
import { UserProfile } from '../interfaces';

export const useUserServices = (): {
  getCompanies: (filterByMarket?: boolean) => Promise<Company[]>;
  getUserDetails: () => Promise<User>;
  getEmployee: (companyId: string) => Promise<CompanyEmployee>;
} => {
  const { user } = useAuth();
  const { appApiUrl } = React.useContext(AuthAppContext);

  const getUserApiUrl = (userId?: string): string => {
    if (!appApiUrl) {
      throw new Error('Method not implemented.');
    }

    return `${appApiUrl.replace(/\/$/, '')}/api/users/${userId}`;
  };

  const [userDetails, setUserDetails] = React.useState<User>();

  const getCompanies = async (filterByMarket = true): Promise<Company[]> => {
    if (!user) {
      throw new Error('The user is not authenticated');
    }

    const config = !filterByMarket
      ? {
          headers: { [MarketHeader]: null }
        }
      : undefined;

    return axios
      .get<Company[]>(`${getUserApiUrl(user.id)}/companies`, config)
      .then((response) => response.data);
  };

  const getEmployee = async (companyId: string): Promise<CompanyEmployee> => {
    if (!user) {
      throw new Error('The user is not authenticated');
    }

    return axios
      .get<CompanyEmployee>(`${getUserApiUrl(user.id)}/companies/${companyId}`)
      .then((response) => response.data);
  };

  const getUserDetails = async (): Promise<User> => {
    if (!user) {
      throw new Error('The user is not authenticated');
    }

    if (userDetails?.id == user.id) {
      return userDetails;
    }

    try {
      const response = await axios.get<User>(`${getUserApiUrl(user.id)}`, {
        withCredentials: true
      });
      if (!response.data.phones || response.data.phones.length == 0) {
        response.data.phones = getUserProfilePhones(user);
      }
      setUserDetails(response.data);
      return response.data;
    } catch {
      const { role, id, email, firstName, lastName } = user;
      return {
        phones: getUserProfilePhones(user),
        roles: [role],
        id,
        email,
        firstName,
        lastName
      };
    }
  };

  const getUserProfilePhones = (user: UserProfile): Phone[] => {
    return user?.mobilePhone && user.mobilePhone !== ''
      ? [
          {
            phoneType: PhoneType.Mobile,
            number: user.mobilePhone
          }
        ]
      : [];
  };

  return {
    getCompanies,
    getEmployee,
    getUserDetails
  };
};
